import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "redux/store";
import {
  deleteInvoice,
  getBookingDetails,
  postChatBooking,
} from "redux/reducers/Properties/PropertiesSlice";
import { updateBookingsDetails } from "redux/reducers/Payments/PaymentReducer";
import { getMessage } from "redux/reducers/UserSlice/UserSlice";
import { BookingRequestDetails } from "redux/reducers/Properties/PropertiesSliceTypes";
import successHandler from "utils/helpers/SuccessHandler";
import { normalizeStatus } from "./bookingStatus";

/**
 * All data + actions for the booking page. Logic is copied from the old
 * BookingRequest.tsx so behaviour is identical; only the UI is new.
 */
export const useBookingRequestPage = (idOverride?: string | null) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const id = idOverride ?? new URLSearchParams(location.search).get("id");
  const { currentUser } = useSelector((state: RootState) => state.user);

  const [booking, setBooking] = useState<BookingRequestDetails | any>(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  // reply composer
  const [replyText, setReplyText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const chatId = booking?.chat?.id;
  const status = normalizeStatus(booking?.status);
  const isHost = !!currentUser?.is_host;

  // Same date parsing as the old page ("January 05, 2027").
  const checkInDate = booking?.check_in_date
    ? moment(booking.check_in_date, "MMMM DD, YYYY").toDate()
    : new Date();
  const checkOutDate = booking?.check_out_date
    ? moment(booking.check_out_date, "MMMM DD, YYYY").toDate()
    : new Date();

  const isReviewEnabled =
    booking?.check_in_date && dayjs().isBefore(dayjs(booking.check_in_date), "day")
      ? false
      : !!booking?.check_out_date &&
        (dayjs().isAfter(dayjs(booking.check_out_date).subtract(15, "day"), "day") ||
          dayjs().isAfter(dayjs(booking.check_out_date), "day"));

  const refresh = useCallback(async () => {
    const action: any = await dispatch(getBookingDetails(id));
    if (action?.payload?.success) setBooking(action.payload.booking);
  }, [dispatch, id]);

  const fetchMessages = useCallback(
    async (page: number) => {
      setLoadingMessages(true);
      try {
        const response = await dispatch(getMessage({ page, chatId })).unwrap();
        setChatMessages(response || []);
      } catch (e) {
        // keep previous messages
      } finally {
        setLoadingMessages(false);
      }
    },
    [dispatch, chatId]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refresh();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    if (chatId) fetchMessages(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // ---- composer (same file-preview rules as the old page) ----
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);

    newFiles.forEach((file) => {
      const t = file.type;
      const push = (p: any) => setPreviews((prev) => [...prev, p]);
      const readUrl = (type: string) => {
        const r = new FileReader();
        r.onloadend = () => push({ type, content: r.result as string, name: file.name });
        r.readAsDataURL(file);
      };
      if (t.startsWith("image/")) readUrl("image");
      else if (t === "application/pdf") push({ type: "pdf", name: file.name });
      else if (t.startsWith("video/")) {
        if (file.size > 5 * 1024 * 1024) {
          toast("Video file size should not exceed 5 MB");
          return;
        }
        readUrl("video");
      } else if (/msword|wordprocessingml|ms-word/.test(t)) push({ type: "doc", name: file.name });
      else if (/ms-excel|spreadsheetml/.test(t)) push({ type: "excel", name: file.name });
      else if (/ms-powerpoint|presentationml/.test(t)) push({ type: "powerpoint", name: file.name });
      else if (t === "text/plain" || t === "text/csv") {
        const r = new FileReader();
        r.onloadend = () => push({ type: "text", content: r.result as string, name: file.name });
        r.readAsText(file);
      } else if (t === "application/zip" || t === "application/x-zip-compressed") push({ type: "zip", name: file.name });
      else push({ type: "generic", name: file.name, extension: file.name.split(".").pop() || "" });
    });
    e.target.value = ""; // allow picking the same file again
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!replyText.trim() && selectedFiles.length === 0) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("message[body]", replyText);
      selectedFiles.forEach((f) => formData.append("message[attachments][]", f));
      await dispatch(postChatBooking({ chatId, formData })).unwrap();
      setReplyText("");
      setSelectedFiles([]);
      setPreviews([]);
      refresh();
      fetchMessages(1);
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- booking actions ----
  const cancelBooking = async () => {
    if (id == null) return;
    setIsCanceling(true);
    try {
      const action: any = await dispatch(updateBookingsDetails({ id, status: "canceled" }));
      if (updateBookingsDetails.fulfilled.match(action)) {
        successHandler("Booking canceled successfully:", action.payload);
        await refresh();
        fetchMessages(1);
      } else {
        console.error("Failed to cancel booking:", action.payload);
      }
    } catch (error) {
      console.error("Error canceling booking:", error);
    } finally {
      setIsCanceling(false);
    }
  };

  const removeInvoice = async () => {
    if (id == null) return;
    setIsDeleting(true);
    try {
      const action: any = await dispatch(
        deleteInvoice({ bookingId: id, id: booking?.invoice?.id, _destroy: true, status: "canceled" })
      );
      if (deleteInvoice.fulfilled.match(action)) {
        successHandler("Invoice deleted successfully:", action.payload);
        await refresh();
        fetchMessages(1);
      } else {
        console.error("Failed to delete invoice:", action.payload);
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    id, booking, loading, status, isHost, currentUser,
    checkInDate, checkOutDate, isReviewEnabled,
    refresh, fetchMessages, chatMessages, loadingMessages,
    composer: { replyText, setReplyText, selectedFiles, previews, isSubmitting, handleFileChange, handleRemoveFile, handleSubmit },
    actions: { cancelBooking, removeInvoice, isDeleting, isCanceling },
  };
};
